import {
  memoryNodeDetail,
  memoryNodeLabel,
  memoryNodeType,
  type EdgeKind,
  type MemLink,
  type MemType,
  type MemoryGraphApi,
} from './memory-graph.ts';
import type { LogMemRecord, SessionLogEvent } from '../../agents/sessionLog.ts';

/**
 * Projections of the {@link SessionLogEvent} log onto the inspector surfaces.
 *
 * The log is the single source of truth (see `sessionLog.ts`); these are pure,
 * deterministic functions of it. Today only the graph projects from the log; the
 * "All" list still reads the full knowledge base from `/agent-memory` (a separate
 * source by design).
 */

const PROMPT_LABEL_MAX = 22;

function promptLabel(text: string): string {
  const t = text.trim();
  return `“${t.slice(0, PROMPT_LABEL_MAX)}${t.length > PROMPT_LABEL_MAX ? '…' : ''}”`;
}

/** A turn's aggregated tool node label + a per-tool breakdown for its popover. */
function toolLabel(count: number): string {
  return `tools ×${count}`;
}
function toolDetail(names: readonly string[]): string {
  const counts = new Map<string, number>();
  for (const n of names) {
    counts.set(n, (counts.get(n) ?? 0) + 1);
  }
  const lines = [...counts.entries()].map(([n, c]) => (c > 1 ? `${n} ×${c}` : n));
  return `Episodic · tool calls this turn\n\n${lines.join('\n')}`;
}

/** Graph node type for a recalled/written record. */
function recordType(rec: LogMemRecord): MemType {
  return memoryNodeType(rec.kind);
}

/**
 * Reconcile the memory graph to the session log: the graph becomes exactly the
 * conversation's turns (prompt → aggregated tool node → written + recalled
 * records), chained turn-to-turn, with the **latest turn lit** ("in context").
 *
 * Reconciles rather than rebuilds, so node positions (physics state) survive: each
 * node carries a stable key (the event/record id), `addMemory` is idempotent on
 * it, and {@link MemoryGraphApi.retainRecords} prunes anything the log no longer
 * contains. Edges are created once (idempotent re-adds don't duplicate them). This
 * is the ONLY writer of the graph — `useAgentChat` no longer mutates it directly.
 */
export function logToGraph(memory: MemoryGraphApi, log: readonly SessionLogEvent[]): void {
  const idByKey = new Map<string, number>();
  const keep = new Set<string>();
  /** Node keys grouped by turn, so the last turn can be lit as in-context. */
  const turnKeys: string[][] = [];

  let promptKey: string | null = null;
  let prevPromptKey: string | null = null;
  let toolNames: string[] = [];

  const add = (
    key: string,
    label: string,
    type: MemType,
    links: readonly { toKey: string; kind: EdgeKind }[],
    detail: string,
  ): void => {
    keep.add(key);
    if (turnKeys.length > 0) {
      turnKeys[turnKeys.length - 1].push(key);
    }
    const resolved: MemLink[] = links
      .map((l): MemLink | null => {
        const to = idByKey.get(l.toKey);
        return to === undefined ? null : { to, kind: l.kind };
      })
      .filter((l): l is MemLink => l !== null);
    idByKey.set(key, memory.addMemory(label, type, resolved, detail, key));
  };

  for (const ev of log) {
    switch (ev.type) {
      case 'user-message': {
        turnKeys.push([]);
        toolNames = [];
        const key = `p:${ev.id}`;
        const links = prevPromptKey === null ? [] : [{ toKey: prevPromptKey, kind: 'session' as const }];
        add(key, promptLabel(ev.text), 'prompt', links, 'Episodic · your prompt this turn.');
        promptKey = key;
        prevPromptKey = key;
        break;
      }
      case 'tool-call': {
        if (promptKey === null) {
          break;
        }
        toolNames.push(ev.name);
        const key = `t:${promptKey}`;
        const label = toolLabel(toolNames.length);
        const detail = toolDetail(toolNames);
        const existing = idByKey.get(key);
        if (existing === undefined) {
          add(key, label, 'tool', [{ toKey: promptKey, kind: 'derives' }], detail);
        } else {
          // One aggregated tool node per turn — grow its count in place.
          memory.setNodeText(existing, label, detail);
          keep.add(key);
          turnKeys[turnKeys.length - 1].push(key);
        }
        break;
      }
      case 'memory-write':
      case 'memory-recall': {
        if (promptKey === null) {
          break;
        }
        // Written records DERIVE from the turn (solid edge); recalled records are
        // MENTIONED by it (dashed) — pulled in from the knowledge base, not produced.
        const kind: EdgeKind = ev.type === 'memory-recall' ? 'mentions' : 'derives';
        for (const rec of ev.records) {
          add(
            rec.id,
            memoryNodeLabel(rec.text),
            recordType(rec),
            [{ toKey: promptKey, kind }],
            memoryNodeDetail(rec.tier, rec.kind, rec.text),
          );
        }
        break;
      }
      // assistant-message carries no graph node — the turn's memory records (and
      // the chat panel) represent the reply; a duplicate node would only clutter.
    }
  }

  memory.retainRecords(keep);

  // Light the latest turn as "in context"; dim everything older.
  const lastTurn = turnKeys[turnKeys.length - 1] ?? [];
  const litIds = lastTurn
    .map((k) => idByKey.get(k))
    .filter((n): n is number => n !== undefined);
  memory.setContext(litIds);
}
